import type {
    CredentialRotationAttemptState,
    CredentialType,
    Status,
} from '@pillar-portal/database/dist/enums';
import type { OpaqueCursorPagination } from './authorization.types';
import type { SupportedCredentialSystemId } from './secret.types';

export const EncryptionKeyCloudVendor = {
    GCP: 'gcp',
} as const;

export type EncryptionKeyCloudVendor =
    (typeof EncryptionKeyCloudVendor)[keyof typeof EncryptionKeyCloudVendor];

export type GcpEncryptionKeySecretManagerReference = {
    cloud_vendor: typeof EncryptionKeyCloudVendor.GCP;
    family: string;
    sequence_number: number;
};

export type EncryptionKeySecretManagerReference =
    GcpEncryptionKeySecretManagerReference;

export type RotateEncryptionKeyResponse = {
    secret_manager_reference: EncryptionKeySecretManagerReference;
    secret_name: string;
    encryption_algorithm: 'AES-256-GCM';
    status: typeof Status.ACTIVE;
};

export type RekeyEncryptionKeyUsageRequest = {
    limit?: number;
    dry_run?: boolean;
};

export type EncryptionKeyUsageRekeyCounts = {
    credentials: number;
    credential_rotation_attempts: number;
};

export type RekeyEncryptionKeyUsageResponse = {
    active_secret_name: string;
    active_secret_manager_reference: EncryptionKeySecretManagerReference;
    dry_run: boolean;
    scanned: EncryptionKeyUsageRekeyCounts;
    rekeyed: EncryptionKeyUsageRekeyCounts;
    skipped_concurrent_updates: EncryptionKeyUsageRekeyCounts;
    remaining: EncryptionKeyUsageRekeyCounts;
};

export type EncryptionKeyUsageCounts = {
    credentials: number;
    credential_rotation_attempts: number;
};

export type EncryptionKeyListItem = {
    secret_name: string;
    secret_manager_reference: EncryptionKeySecretManagerReference;
    encryption_algorithm: string;
    status: Status;
    usage_counts: EncryptionKeyUsageCounts;
};

export type ListEncryptionKeysRequest = {
    limit?: number;
    cursor?: string;
};

export type ListEncryptionKeysResponse = {
    encryption_keys: EncryptionKeyListItem[];
    pagination: OpaqueCursorPagination;
};

export type EncryptionKeyCredentialListItem = {
    credential_uuid: string;
    organization_uuid: string;
    system_id: SupportedCredentialSystemId;
    account_id: string;
    credential_type: CredentialType;
    credential_label: string | null;
    public_fields: Record<string, unknown>;
    masked_preview: string;
    status: Status;
    applies_to_children: boolean;
};

export type ListEncryptionKeyCredentialsRequest = {
    limit?: number;
    cursor?: string;
};

export type ListEncryptionKeyCredentialsResponse = {
    credentials: EncryptionKeyCredentialListItem[];
    pagination: OpaqueCursorPagination;
};

export type EncryptionKeyCredentialRotationAttemptListItem = {
    credential_rotation_attempt_uuid: string;
    credential: EncryptionKeyCredentialListItem;
    organization_uuid: string;
    state: CredentialRotationAttemptState;
    flow_key: string;
    target_field_keys: string[];
    credential_definition_digest: string;
    ipx_web_resource_model_digest: string | null;
    created_ts: string;
    expires_ts: string;
    injected_ts: string | null;
    submitted_ts: string | null;
    promoted_ts: string | null;
    failed_ts: string | null;
};

export type ListEncryptionKeyCredentialRotationAttemptsRequest = {
    limit?: number;
    cursor?: string;
};

export type ListEncryptionKeyCredentialRotationAttemptsResponse = {
    credential_rotation_attempts: EncryptionKeyCredentialRotationAttemptListItem[];
    pagination: OpaqueCursorPagination;
};
